import BigNumber from "bignumber.js";
import { Dispatch, SetStateAction } from "react";
import { useTranslation } from "src/hooks";
import { TableColumnKey } from "src/views/Ledger/LedgerBalanceMonitor/types/Table";
import { AlertType } from "src/api/types";
import { BalanceMonitorThresholdFields } from "./types";
import { Box } from "src/components/MuiGenerals";
import { usePageConfigProviderContext } from "src/features/PageConfigProvider";
import LowerThresholdTextField from "src/components/Common/BalanceThresholdInput/LowerThresholdTextField";
import UpperThresholdTextField from "src/components/Common/BalanceThresholdInput/UpperThresholdTextField";
import { displayAmount } from "src/utils/helper";

function ThresholdFieldSection<T extends BalanceMonitorThresholdFields>({
  fields,
  setFields,
  isLowerThresholdInvalid,
  isUpperThresholdInvalid,
  helperTextKey = "fixed_threshold_message",
  dynamicHelperTextKey = "dynamic_threshold_message",
}: {
  fields: T;
  setFields: Dispatch<SetStateAction<T>>;
  isLowerThresholdInvalid: boolean;
  isUpperThresholdInvalid: boolean;
  helperTextKey?: string;
  dynamicHelperTextKey?: string;
}) {
  const { translatePrefix } = usePageConfigProviderContext();
  const { t } = useTranslation(translatePrefix);
  const { alertType, balance, interval, thresholdLower, thresholdUpper } =
    fields;

  const array = [
    {
      label: t(TableColumnKey.ThresholdLower),
      component:
        alertType === AlertType.FixedThreshold ? (
          <LowerThresholdTextField
            fieldName={t(TableColumnKey.ThresholdLower)}
            value={thresholdLower || ""}
            onChange={(value) =>
              setFields((prev) => ({ ...prev, thresholdLower: value }))
            }
            isInvalid={isLowerThresholdInvalid}
          />
        ) : (
          <div style={{ fontSize: "12px" }}>
            {displayAmount(
              BigNumber.max(
                new BigNumber(balance ?? 0).minus(interval || 0),
                0,
              ).toString(),
              0,
            )}
          </div>
        ),
    },
    {
      label: t(TableColumnKey.ThresholdUpper),
      component:
        alertType === AlertType.FixedThreshold ? (
          <UpperThresholdTextField
            fieldName={t(TableColumnKey.ThresholdUpper)}
            value={thresholdUpper || ""}
            onChange={(value) =>
              setFields((prev) => ({ ...prev, thresholdUpper: value }))
            }
            isInvalid={isUpperThresholdInvalid}
          />
        ) : (
          <div style={{ fontSize: "12px" }}>
            {displayAmount(
              new BigNumber(balance ?? 0).plus(interval || 0).toString(),
              0,
            )}
          </div>
        ),
    },
  ];

  return (
    <div>
      <Box
        sx={{
          width: "100%",
          display: "flex",
          flexDirection: "row",
          gap: "20px",
          minHeight: "55.063px",
        }}
      >
        {array.map((item, index) => (
          <div
            key={index}
            style={{
              width: "100%",
              display: "grid",
              gridTemplateColumns: "100px 1fr",
              gap: "8px",
              alignItems: "center",
            }}
          >
            <div style={{ fontSize: "12px", lineHeight: "2rem" }}>
              {item.label}
            </div>
            {item.component}
          </div>
        ))}
      </Box>
      <div
        style={{
          width: "100%",
          display: "grid",
          gridTemplateColumns: "100px 1fr",
          gap: "8px",
          marginTop: "1rem",
        }}
      >
        <div style={{ gridColumn: 2, fontSize: 12, color: "#A2A2A2" }}>
          {alertType === AlertType.FixedThreshold
            ? t(helperTextKey)
            : t(dynamicHelperTextKey, { interval: interval || 0 })}
        </div>
      </div>
    </div>
  );
}

export default ThresholdFieldSection;
